import { Box, Button, Heading, Icon, Modal, Text } from '@zillow/constellation-10';
import { type TenantInfoDto as Tenant } from '@zg-rentals/ts-rins-client';
import { IconMailFilled } from '@zillow/constellation-icons';
import { useProofOfInsuranceWebApiHandler_sendProofRequestEmail } from '@zg-rentals/ts-rins-client-react-query';

const TOAST_MESSAGE = {
  SEND_SUCCESS: 'The request has been sent to the tenant',
  SEND_ERROR: 'Failed to send request. Please try again.',
} as const;

const MODAL_CONTENT = {
  TITLE: 'Contacting a tenant',
  LABEL: 'Send an email requesting proof of insurance',
  HELP1:
    "If your tenant hasn't provided you with proof of their renters insurance policy, contacting them to request this information helps you ensure that coverage is in place to reduce your liability and prevent certain property damage expenses.",
  HELP2:
    'We’ll automatically create a message with this information, as well as share options with them for how to purchase renters insurance.',
  PRIMARY_CTA_LABEL: 'Send request',
} as const;

type ContactTenantModalProps = {
  listingAlias: string;
  isOpen: boolean;
  tenant: Tenant;
  onClose: () => void;
  onSuccess?: (message: string) => void | null;
  onError?: (message: string) => void | null;
};

const ContactTenantModal = ({ listingAlias, isOpen, tenant, onClose, onSuccess, onError }: ContactTenantModalProps) => {
  const { mutate: sendProofRequestEmail } = useProofOfInsuranceWebApiHandler_sendProofRequestEmail({
    onError: () => {
      onError?.(TOAST_MESSAGE.SEND_ERROR);
    },
    onSuccess: () => {
      onSuccess?.(TOAST_MESSAGE.SEND_SUCCESS);
    },
  });

  const handleSendRequest = () => {
    sendProofRequestEmail({
      postData: {
        listingAlias,
        tenants: [tenant],
      },
    });
    onClose();
  };

  const renderBody = () => {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
        <Icon render={<IconMailFilled />} size="lg" style={{ color: 'blue' }} />
        <Text textStyle="heading-sm">{MODAL_CONTENT.LABEL}</Text>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Text textStyle="body">{MODAL_CONTENT.HELP1}</Text>
          <Text textStyle="body">{MODAL_CONTENT.HELP2}</Text>
        </Box>
      </Box>
    );
  };

  return (
    <Modal
      open={isOpen}
      size="md"
      scrollDocument
      header={
        <Heading data-testid="ProofAddTenantsModal-Header" level={2}>
          {MODAL_CONTENT.TITLE}
        </Heading>
      }
      body={renderBody()}
      footer={
        <Button
          data-testid="ProofAddTenantsModal-Button"
          tone="brand"
          emphasis="filled"
          size="md"
          onClick={handleSendRequest}
        >
          {MODAL_CONTENT.PRIMARY_CTA_LABEL}
        </Button>
      }
      onOpenChange={onClose}
    />
  );
};

export default ContactTenantModal;
